import { useSelector } from 'react-redux'

import type { AlternativeOptions } from '@features/recipe-tile'
import type { RecipeCustomisations, RecipeQueryResponse } from '@library/api-menu-experience'
import type { Recipe, RecipeCoreId, RecipeUUID } from '@library/menu-service'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useCollection, useCurrentCollectionId } from 'hooks/menu-collections'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useRecipeDetailData } from 'routes/Menu/components/Detail/hooks/useRecipeDetailData'
import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

const hasAlternativeOptions = (customisations: RecipeCustomisations): boolean => {
  if (customisations.type === 'ingredients') {
    return customisations.slots.some((slot) => slot.values.length > 1)
  }

  if (customisations.type === 'recipes') {
    return customisations.options.length > 1
  }

  return false
}

const buildAlternativeOptions = ({
  mainRecipe,
  experienceRecipe,
  menuRecipes,
  basketHasRecipe,
}: {
  mainRecipe: Recipe
  experienceRecipe: RecipeQueryResponse
  menuRecipes: Recipe[]
  basketHasRecipe: (recipeId: RecipeCoreId) => boolean
}): AlternativeOptions => {
  const options = menuRecipes.map((recipe) => {
    const hasUnavailableIngredientSelection =
      experienceRecipe.customisations.type === 'ingredients' &&
      experienceRecipe.customisations.slots.some((slot) =>
        slot.values.some((value) => value.recipeId === recipe.id && !value.isAvailable),
      )

    const recipeCustomisationOption =
      experienceRecipe.customisations.type === 'recipes'
        ? experienceRecipe.customisations.options.find((option) => option.recipeId === recipe.id)
        : undefined

    return {
      recipe,
      recipeId: recipe.coreId,
      recipeName: recipe.name,
      isChecked: mainRecipe.id === recipe.id,
      isOutOfStock:
        (!recipe.isAvailable ||
          hasUnavailableIngredientSelection ||
          Boolean(recipeCustomisationOption && !recipeCustomisationOption.isAvailable)) &&
        !basketHasRecipe(recipe.coreId),
      surcharge: recipe.surcharge ? recipe.surcharge.pricePerPortion / 100 : null,
      surchargeLabel: recipeCustomisationOption?.surcharge?.perPortionSurcharge || null,
    }
  })

  const hasAlternativeChoices = hasAlternativeOptions(experienceRecipe.customisations)

  return {
    experienceIngredientSlots:
      hasAlternativeChoices && experienceRecipe.customisations.type === 'ingredients'
        ? experienceRecipe.customisations.slots.map((slot) => ({
            text: slot.text,
            values: slot.values.map((value) => ({
              recipeId: value.recipeId,
              name: value.name,
              isAvailable: value.isAvailable,
            })),
          }))
        : undefined,
    options: hasAlternativeChoices ? options : null,
    optionsType: hasAlternativeChoices ? experienceRecipe.customisations.type : 'none',
  }
}

const getOptionIds = (recipeId: RecipeUUID, customisations: RecipeCustomisations): RecipeUUID[] => {
  if (customisations.type === 'ingredients') {
    return Array.from(
      new Set([
        recipeId,
        ...customisations.slots.flatMap((slot) =>
          slot.values
            .map((value) => value.recipeId)
            .filter((optionRecipeId): optionRecipeId is RecipeUUID => Boolean(optionRecipeId)),
        ),
      ]),
    )
  }

  if (customisations.type === 'recipes') {
    return customisations.options.map((option) => option.recipeId)
  }

  return []
}

export const useRecipeOptions = (
  mainRecipe: Recipe,
  shouldFetchOptions: boolean,
): {
  isLoadingOptions: boolean
  alternativeOptions: AlternativeOptions
} => {
  const { basketHasRecipe } = useBasketRecipes()

  const numPortions = useSelector(getNumPortions)
  const deliveryDate = useSelector(getBasketDate)
  const menuId = useSelector(getBasketMenuUuid)
  const collectionId = useCurrentCollectionId()
  const collection = useCollection(collectionId)

  const baseRecipe =
    collection?.recipes.find((recipe) => recipe.id === mainRecipe.id) ||
    collection?.recipes.find((recipe) =>
      recipe.options.some((option) => option.id === mainRecipe.id),
    )

  const defaultOptionsType = baseRecipe?.optionsType || 'none'

  const {
    loading,
    recipe: experienceRecipe,
    error,
  } = useRecipeDetailData({
    menuId,
    recipeUuid: mainRecipe.id,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
  })

  const optionIds =
    shouldFetchOptions && experienceRecipe
      ? getOptionIds(mainRecipe.id, experienceRecipe.customisations)
      : []
  const menuRecipes: Recipe[] = useRecipes(optionIds).filter(
    (recipe): recipe is Recipe => recipe !== null,
  )

  if (!shouldFetchOptions || !experienceRecipe || error) {
    return {
      isLoadingOptions: loading,
      alternativeOptions: {
        optionsType: defaultOptionsType,
        options: null,
      },
    }
  }

  return {
    isLoadingOptions: loading,
    alternativeOptions: buildAlternativeOptions({
      mainRecipe,
      experienceRecipe,
      menuRecipes,
      basketHasRecipe,
    }),
  }
}
